import { useState } from 'react'
import { AssetReference } from '@opallabs/cassini'
import { kuiperClassNames } from '@opallabs/kuiper'
import { ModularCarousel } from '../../../ModularCarousel'
import { doNothing } from '../../helpers/functions/doNothing'
import getAssetOrientation from '../../helpers/functions/getAssetOrientation'
import { useValidModules } from '../../helpers/hooks'
import { Module, OnDragDropProps } from '../../helpers/types'
import { MobileStream } from './MobileStream'
import { styles } from './styles'

const getAssetHeight = (asset: AssetReference) => {
  let orientation = 'square'
  let assetHeight = 375
  if (asset) {
    orientation = getAssetOrientation(asset)
    if (orientation === 'landscape') {
      assetHeight = 196
    } else if (orientation === 'portrait') {
      assetHeight = 469
    }
  }
  return { orientation, assetHeight }
}

type Props = {
  accountImage: AssetReference
  accountUsername: string
  collabAccount: string
  content: string
  instagramCollab: boolean
  isExpanded: boolean
  isPresenting: boolean
  location: string
  modules: Module[]
  notCropped: boolean
  onAddSection?: () => void
  onDeleteSection?: () => void
  onDragDrop?: OnDragDropProps
  onExpandAsset?: () => void
  onRemoveAsset?: () => void
  onShowAssetPicker?: () => void
  postType: string
  readOnly: boolean
  truncate: boolean
  updateSectionPostOption?: () => void
  updateText?: (val: string, dest: string) => void
}

export const InstagramCarousel: React.FC<Props> = ({
  accountImage,
  accountUsername,
  collabAccount,
  content,
  instagramCollab,
  isExpanded,
  isPresenting = false,
  location,
  modules = [],
  notCropped,
  onAddSection,
  onDeleteSection,
  onDragDrop,
  onExpandAsset,
  onRemoveAsset,
  onShowAssetPicker,
  postType,
  readOnly = false,
  truncate,
  updateSectionPostOption,
  updateText,
}) => {
  const [height, setHeight] = useState(670)
  const firstAsset =
    modules && modules[0] && (modules[0].igCarouselAsset as AssetReference)
  const { orientation, assetHeight } = getAssetHeight(firstAsset)

  const validModules = useValidModules({
    modules,
    onAddSection: onAddSection ? onAddSection : doNothing,
    componentNames: ['ig_car_section'],
    minModulesLength: 1,
  })

  const availableModules = [
    {
      component: MobileStream,
      componentName: 'ig_car_section',
      title: '',
    },
  ]

  const onContentChange = (val: string) => {
    if (!updateText) return
    updateText(val, 'content')
  }

  const onFooterResize = (footerHeight: number) => {
    const viewportHeight = notCropped ? assetHeight : 375
    const newHeight = 150 + viewportHeight + footerHeight
    setHeight(newHeight)
  }

  return (
    <div
      css={styles.getBaseStyles(isExpanded, height, orientation, notCropped)}
      className={kuiperClassNames(
        'instagram instagram-carousel instagram-mobile-stream'
      )}
    >
      <ModularCarousel
        addComponentText="Add Carousel Section"
        allProps={{
          accountImage,
          accountUsername,
          assetHeight,
          collabAccount,
          content,
          instagramCollab,
          location,
          notCropped,
          onContentChange,
          onFooterResize,
          postType,
          truncate,
        }}
        overrideStyles={{
          activeViewportStyles: styles.getActiveViewport,
          arrowStyles: () => styles.getArrowHeight(notCropped, orientation),
          viewportStyles: () =>
            styles.getViewport(readOnly, notCropped, assetHeight),
          sectionStyles: styles.getSection,
        }}
        isExpanded={isExpanded}
        availableModules={availableModules}
        carouselWidth={375}
        modules={validModules}
        maxSections={20}
        onDragDrop={onDragDrop ? onDragDrop : doNothing}
        onAddSection={onAddSection ? onAddSection : doNothing}
        onDeleteSection={onDeleteSection ? onDeleteSection : doNothing}
        onExpandAsset={onExpandAsset ? onExpandAsset : doNothing}
        onRemoveAsset={onRemoveAsset ? onRemoveAsset : doNothing}
        onShowAssetPicker={onShowAssetPicker ? onShowAssetPicker : doNothing}
        updateSectionPostOption={
          updateSectionPostOption ? updateSectionPostOption : doNothing
        }
        readOnly={readOnly}
        isPresenting={isPresenting}
      />
    </div>
  )
}
